import * as React from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useFocusTrap } from '@/lib/use-focus-trap';

// =====================================================================
// Shared modal primitive (3B-3.5d v3).
//
// All chooser-flow modals (AI chat, starter-stack picker, layout
// picker) render through this shell so they share the same frame:
// max-w-3xl, identical header/body/footer rhythm, identical close
// behavior (X / backdrop / ESC), identical focus restore.
//
// Body slot scrolls independently when a footer is provided so the
// AI conversation can grow without pushing the composer off-screen.
// =====================================================================

const SIZE_CLASS: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
};

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  // Optional small icon shown left of the title (e.g. sage Sparkles
  // for the AI modal). Render at h-4 w-4 inside the badge slot.
  titleBadge?: React.ReactNode;
  // Width cap. Defaults to '3xl' to match the layout picker.
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';
  // Body content. Renders inside the standard px-6 py-5 padding.
  children: React.ReactNode;
  // Optional sticky footer (e.g. Cancel button, AI composer). When
  // provided, the body scrolls independently above it.
  footer?: React.ReactNode;
  // While submitting, block all close affordances (X, backdrop, ESC).
  disableClose?: boolean;
  // ARIA id; auto-derived from title if not provided. Useful when
  // multiple modals could mount simultaneously in test scenarios.
  labelledById?: string;
}

export function Modal({
  open,
  onClose,
  title,
  description,
  titleBadge,
  size = '3xl',
  children,
  footer,
  disableClose = false,
  labelledById,
}: ModalProps) {
  const cardRef = React.useRef<HTMLDivElement | null>(null);
  const restoreFocusRef = React.useRef<HTMLElement | null>(null);
  const headingId = labelledById ?? `modal-title-${useStableId()}`;

  // Keep Tab focus inside the dialog — aria-modal promises it, the
  // browser doesn't enforce it.
  useFocusTrap(cardRef, open);

  React.useEffect(() => {
    if (!open) return;
    restoreFocusRef.current = (document.activeElement as HTMLElement | null) ?? null;

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !disableClose) {
        e.stopPropagation();
        onClose();
      }
    }
    document.addEventListener('keydown', onKey);

    // Focus the first interactive element inside the card.
    requestAnimationFrame(() => {
      const first = cardRef.current?.querySelector<HTMLElement>(
        'input, textarea, button:not([aria-label="Close"]), [tabindex]:not([tabindex="-1"])',
      );
      first?.focus();
    });

    return () => {
      document.removeEventListener('keydown', onKey);
      restoreFocusRef.current?.focus?.();
    };
  }, [open, disableClose, onClose]);

  if (!open) return null;

  const hasFooter = footer !== undefined && footer !== null && footer !== false;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={headingId}
      className="fixed inset-0 z-50 overflow-y-auto"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        disabled={disableClose}
        className="fixed inset-0 z-0 bg-text-primary/40 backdrop-blur-sm"
      />

      <div className="relative z-10 flex min-h-full items-center justify-center px-4 py-8 sm:py-12">
        <div
          ref={cardRef}
          className={cn(
            'flex w-full flex-col rounded-2xl bg-card shadow-lg',
            // Locked size + position so all chooser modals (AI / starter
            // stack / layouts) open at the exact same viewport footprint.
            // 48rem chosen so the layout-picker's 6-card 3-col grid
            // (densest content) fits without internal scroll on a typical
            // laptop. The other modals inherit this size — their lighter
            // content just leaves more breathing room. Viewport cap
            // kicks in only on short screens.
            'h-[48rem] max-h-[calc(100vh-4rem)]',
            'animate-[panel-enter_180ms_ease-out]',
            SIZE_CLASS[size],
          )}
        >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 border-b border-border-subtle px-6 py-4">
          <div className="flex min-w-0 items-start gap-2.5">
            {titleBadge ? (
              <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-700">
                {titleBadge}
              </span>
            ) : null}
            <div className="min-w-0">
              <h3
                id={headingId}
                className="text-lg font-semibold tracking-tight text-foreground"
              >
                {title}
              </h3>
              {description ? (
                <p className="mt-1 text-sm text-muted-foreground">{description}</p>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={disableClose}
            className={cn(
              'flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-text-secondary',
              'transition-colors hover:bg-surface-sunken hover:text-foreground',
              'focus-visible:bg-surface-sunken focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              'disabled:opacity-50',
            )}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

          {/* Body — always fills remaining card height; scrolls on overflow. */}
          <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>

          {/* Footer (optional) */}
          {hasFooter ? (
            <div className="border-t border-border-subtle px-6 py-4">{footer}</div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// Lightweight stable-id hook (avoids React 18's useId mismatch concerns
// for SSR — not used here, but the pattern is the same and predictable).
let _idCounter = 0;
function useStableId(): string {
  const [id] = React.useState(() => {
    _idCounter += 1;
    return `m${_idCounter}`;
  });
  return id;
}
